import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createRecipeNote } from '../../services/recipeNotes/recipeNotesService';
import { cn } from '../../lib/utils';
import {
  primaryText,
  secondaryText,
  smallTextSize,
} from '../../styling/stylingHelper';

interface RecipeNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipeId: string;
  componentId: string;
  componentDisplayName: string;
  restaurantId: string;
  onSave: () => void;
}

export default function RecipeNoteModal({
  isOpen,
  onClose,
  recipeId,
  componentId,
  componentDisplayName,
  restaurantId,
  onSave,
}: RecipeNoteModalProps) {
  const { t } = useTranslation();
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setContent('');
      setError(null);
      setSaving(false);
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!content.trim()) {
      setError(t('recipes.noteContentRequired'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await createRecipeNote({
        recipeId,
        componentId,
        content: content.trim(),
        restaurantId,
      });
      onSave();
      onClose();
    } catch (err: any) {
      console.error('Failed to create note:', err);
      setError(t('recipes.failedToCreateNote'));
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-md border border-zinc-200 dark:border-white/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-50">
                {t('recipes.addNote')}
              </h2>
              <p className={cn(smallTextSize, secondaryText, 'mt-1')}>
                {componentDisplayName}
              </p>
            </div>
            <button
              onClick={onClose}
              className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
              aria-label={t('common.close')}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <div className="space-y-5">
            <div>
              <label
                htmlFor="noteContent"
                className={cn('block mb-1.5', smallTextSize, primaryText, 'font-medium')}
              >
                {t('recipes.noteContent')}
              </label>
              <textarea
                id="noteContent"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={4}
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:focus:ring-emerald-500/20 focus:border-emerald-500 transition resize-none"
                placeholder={t('recipes.notePlaceholder')}
              />
            </div>

            {/* Error message */}
            {error && (
              <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !content.trim()}
                className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
